import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Key, ArrowRight, CheckCircle, Copy, Terminal, Globe, Shield, Package, Users, Certificate } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const APIDocsPage = () => {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      category: 'Companies',
      icon: Globe,
      color: '#4A6C4C',
      items: [
        { method: 'POST', path: '/api/companies/register', description: 'Register a new company and get API keys' },
        { method: 'GET', path: '/api/companies/{id}', description: 'Get company details' },
        { method: 'GET', path: '/api/companies/{id}/dashboard', description: 'Get company dashboard with stats' },
        { method: 'PUT', path: '/api/companies/{id}/upgrade', description: 'Upgrade subscription tier' },
      ]
    },
    {
      category: 'Certification',
      icon: Certificate,
      color: '#C26D53',
      items: [
        { method: 'GET', path: '/api/templates', description: 'List all industry templates' },
        { method: 'GET', path: '/api/templates/{id}/drivers', description: 'Get drivers for a template' },
        { method: 'POST', path: '/api/certify', description: 'Create a new certification' },
        { method: 'GET', path: '/api/certificates', description: 'List all certificates' },
        { method: 'GET', path: '/api/certificates/{id}', description: 'Get certificate details' },
        { method: 'GET', path: '/api/passport/{id}', description: 'Get public passport data' },
        { method: 'POST', path: '/api/scan/verify', description: 'Verify a certificate by ID or QR' },
      ]
    },
    {
      category: 'Identity',
      icon: Users,
      color: '#D4A373',
      items: [
        { method: 'POST', path: '/api/iris/register', description: 'Register iris identity' },
        { method: 'POST', path: '/api/iris/verify', description: 'Verify iris identity' },
        { method: 'GET', path: '/api/iris/{id}', description: 'Get identity details' },
      ]
    },
    {
      category: 'Supply Chain',
      icon: Package,
      color: '#6B6659',
      items: [
        { method: 'POST', path: '/api/supply-chain/add-stage', description: 'Add supply chain stage' },
        { method: 'GET', path: '/api/supply-chain/{certificate_id}', description: 'Get full supply chain' },
      ]
    },
    {
      category: 'Auditors',
      icon: Shield,
      color: '#2C3E2D',
      items: [
        { method: 'POST', path: '/api/auditors/register', description: 'Register as an auditor' },
        { method: 'GET', path: '/api/auditors', description: 'List verified auditors' },
        { method: 'POST', path: '/api/audits/request', description: 'Request an audit' },
        { method: 'PUT', path: '/api/audits/{id}/complete', description: 'Complete an audit' },
      ]
    },
  ];

  const codeExamples = {
    register: `curl -X POST "https://api.ginsgood.com/api/companies/register" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Artisan Collective",
    "email": "team@artisan.com",
    "industry": "textiles_apparel"
  }'`,
    certify: `curl -X POST "https://api.ginsgood.com/api/certify" \\
  -H "X-API-Key: gsg_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_name": "Handwoven Silk Scarf",
    "product_description": "Traditional handwoven scarf",
    "product_category": "Apparel",
    "seller_iris_id": "iris_id_here",
    "artisan_name": "Master Weaver",
    "artisan_location": "Varanasi, India",
    "material": "100% Silk",
    "material_origin": "Karnataka",
    "craftsmanship_method": "Handloom",
    "industry_template": "textiles_apparel",
    "driver_responses": [
      {"driver_id": "geographic_indication", "selected_option": "Yes - GI Certified"},
      {"driver_id": "craftsman_verification", "selected_option": "Iris Verified"}
    ]
  }'`,
    verify: `curl -X POST "https://api.ginsgood.com/api/scan/verify" \\
  -H "Content-Type: application/json" \\
  -d '{
    "certificate_id": "your_certificate_id"
  }'`
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Code weight="duotone" className="w-4 h-4 mr-2" />
              Developer Resources
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              API Documentation
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Integrate GinsGood certification into your platform. Our RESTful API makes it easy to certify products, 
              verify authenticity, and track supply chains.
            </p>
          </div>

          {/* Quick Start */}
          <Card className="p-8 mb-12 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="font-bold text-primary">1</span>
                </div>
                <div>
                  <h3 className="font-medium mb-1">Register Company</h3>
                  <p className="text-sm text-muted-foreground">Create an account and get your API keys</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="font-bold text-primary">2</span>
                </div>
                <div>
                  <h3 className="font-medium mb-1">Authenticate</h3>
                  <p className="text-sm text-muted-foreground">Add X-API-Key header to all requests</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="font-bold text-primary">3</span>
                </div>
                <div>
                  <h3 className="font-medium mb-1">Start Certifying</h3>
                  <p className="text-sm text-muted-foreground">Use the /api/certify endpoint</p>
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="endpoints" className="space-y-8">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3">
              <TabsTrigger value="endpoints" data-testid="tab-endpoints">Endpoints</TabsTrigger>
              <TabsTrigger value="examples" data-testid="tab-examples">Examples</TabsTrigger>
              <TabsTrigger value="auth" data-testid="tab-auth">Authentication</TabsTrigger>
            </TabsList>

            <TabsContent value="endpoints">
              <div className="space-y-8">
                {endpoints.map(section => {
                  const Icon = section.icon;
                  return (
                    <Card key={section.category} className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${section.color}15`, color: section.color }}
                        >
                          <Icon weight="duotone" className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-medium">{section.category}</h3>
                      </div>

                      <div className="space-y-3">
                        {section.items.map((endpoint, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                            data-testid={`endpoint-${endpoint.path.replace(/[\/{}]/g, '-')}`}
                          >
                            <Badge 
                              variant={endpoint.method === 'GET' ? 'secondary' : 'default'}
                              className={`w-16 justify-center ${endpoint.method === 'POST' ? 'bg-green-600' : endpoint.method === 'PUT' ? 'bg-yellow-600' : ''}`}
                            >
                              {endpoint.method}
                            </Badge>
                            <code className="text-sm font-mono flex-1">{endpoint.path}</code>
                            <span className="text-sm text-muted-foreground hidden md:block">{endpoint.description}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="examples">
              <div className="space-y-6">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Register a Company</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(codeExamples.register, 'register')}
                    >
                      {copiedCode === 'register' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#1E1E1E] text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                    {codeExamples.register}
                  </pre>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Create Certification</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(codeExamples.certify, 'certify')}
                    >
                      {copiedCode === 'certify' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#1E1E1E] text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                    {codeExamples.certify}
                  </pre>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Verify Certificate</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(codeExamples.verify, 'verify')}
                    >
                      {copiedCode === 'verify' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#1E1E1E] text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                    {codeExamples.verify}
                  </pre>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="auth">
              <Card className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Key weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">API Key Authentication</h3>
                    <p className="text-muted-foreground">
                      All API requests require authentication via the X-API-Key header. 
                      You'll receive both live and test API keys when you register your company.
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Terminal className="w-4 h-4" />
                      Header Format
                    </h4>
                    <code className="text-sm bg-background px-3 py-2 rounded block">
                      X-API-Key: gsg_live_your_api_key_here
                    </code>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="p-4 border-green-200 bg-green-50">
                      <h4 className="font-medium text-green-800 mb-2">Live Keys</h4>
                      <p className="text-sm text-green-700">
                        Prefix: <code className="bg-green-100 px-1 rounded">gsg_live_</code><br />
                        Use in production. Counts against your monthly limit.
                      </p>
                    </Card>
                    <Card className="p-4 border-yellow-200 bg-yellow-50">
                      <h4 className="font-medium text-yellow-800 mb-2">Test Keys</h4>
                      <p className="text-sm text-yellow-700">
                        Prefix: <code className="bg-yellow-100 px-1 rounded">gsg_test_</code><br />
                        Use for development. Does not count against limits.
                      </p>
                    </Card>
                  </div>

                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-1">Security Notice</h4>
                    <p className="text-sm text-red-700">
                      Never expose your API keys in client-side code. Always make API calls from your server.
                    </p>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* CTA */}
          <Card className="mt-12 p-8 text-center bg-primary text-primary-foreground">
            <h2 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Get Started?
            </h2>
            <p className="mb-6 opacity-90">
              Register your company and start certifying products in minutes.
            </p>
            <Button variant="secondary" size="lg" className="gap-2" asChild>
              <a href="/company/register">
                Register Now <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default APIDocsPage;
